import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { recruitmentService } from "@/modules/recruitment/infrastructure/recruitment-service"
import { toast } from "sonner"

export function useApplications(jobId?: string, stage?: string) {
  return useQuery({
    queryKey: ["applications", jobId, stage],
    queryFn: () => recruitmentService.getApplicationsForVacancy(jobId, stage),
  })
}

export function useApplication(id: string) {
  return useQuery({
    queryKey: ["application", id],
    queryFn: () => recruitmentService.getApplicationById(id),
    enabled: !!id,
  })
}

export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
    }: {
      jobId: string
      candidateId: string
    }) => recruitmentService.createApplication(jobId, candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application created successfully!")
    },
    onError: (error: Error) => {
      toast.error(
        error.message || "Failed to create application"
      )
    },
  })
}

export function useUpdateApplicationStage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      stage,
      notes,
    }: {
      id: string
      stage: string
      notes?: string
    }) => recruitmentService.updateApplicationStage(id, stage, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application stage updated!")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update stage")
    },
  })
}

export function useDeleteApplication() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => recruitmentService.deleteApplication(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      queryClient.invalidateQueries({ queryKey: ["job-applications"] })
      toast.success("Application deleted!")
    },
    onError: (error: Error) => {
      toast.error(
        error.message || "Failed to delete application"
      )
    },
  })
}
